// Client Delivery and Operations - job cards (step-by-step instructions), keyed by job code.
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Client Delivery and Operations";

export const cards = {
  O1: {
    title: "Onboard a new client",
    before: [
      "A signed agreement",
      "The client scope from the founders",
      "The onboarding checklist template",
    ],
    steps: [
      { text: "Make a copy of the onboarding checklist for this client. It has nine tabs: company and people, brand assets, tech, product and offer, brand discovery, social and content, ad accounts, customer data, and policies", tag: "Checklist ready" },
      { text: "Fill in the first tab: key dates, phases, and who to contact on our side", tag: "First tab done" },
      { text: "On day one, set up the shared folder and the one communication channel", tag: "Set up" },
      { text: "NEVER let passwords go into the checklist. Ask the client to add us as a user, or use a password manager", tag: "Credentials safe" },
      { text: "If a password has already been written down anywhere, ask the client to change it the same day", tag: "Password changed" },
      { text: "Send the checklist to the client, with a date", tag: "Sent" },
      { text: "Chase the Critical items first. Critical means work cannot start without it", tag: "Criticals in" },
      { text: "Confirm ONE named approver and the ONE channel where approvals are written down. Without this, approved means whoever replied", tag: "Approver confirmed" },
      { text: "Confirm the weekly meeting, the report format they want, and who to escalate to", tag: "Cadence agreed" },
      { text: "Send each tab to the division that needs it. Brand assets to Design and Video and Social, tech to Tech and Automation, ad accounts to Paid Growth, brand discovery and product to Growth Systems", tag: "Routed" },
      { text: "Anything Critical that the client does not have becomes a JOB, with a date, for the right division. For example, no tone of voice means Growth Systems writes one", tag: "Gaps are jobs" },
      { stop: "Founders confirm the client is ready to start" },
      { text: "Tell every Division Lead that work can start", tag: "Kickoff sent" },
    ],
    finishedWhen: "Every Critical item is either received or turned into a job with a date, the approver is named, and every division has what it needs.",
  },
  O2: {
    title: "Hand over the divisional scopes",
    before: [
      "The client scope and the divisional scopes, both from the founders",
    ],
    steps: [
      { text: "Receive the divisional scopes from the founders", tag: "Scopes in" },
      { text: "Check every line has: the job, who does it, the date, and the project lead where there is one", tag: "Checked" },
      { text: "If anything is missing, go back to the founders, not to the Division Lead", tag: "Gaps raised" },
      { text: "Send each Division Lead their own scope", tag: "Sent" },
      { text: "The Division Lead has ONE working day to flag any date they cannot meet, or to ask for any other revision", tag: "Window open" },
      { text: "If they flag something, take it to the founders the same day. The founders decide: move the date, change the scope, or add help", tag: "Resolved" },
      { text: "After one working day with nothing flagged, the dates are agreed. Raising it later is too late", tag: "Committed" },
      { stop: "Founders confirm anything flagged is resolved. Only then do dates go to the client" },
      { text: "Put every line on the delivery dashboard", tag: "Tracked" },
      { text: "Share the combined scope with the client where they need to see it", tag: "Shared" },
    ],
    finishedWhen: "Every Division Lead has their scope, has had one working day to flag anything, and every line is on the dashboard.",
  },
  O3: {
    title: "Track the month",
    before: [
      "Agreed divisional scopes on the dashboard",
    ],
    steps: [
      { text: "Every morning: what is due today, across every division", tag: "Today's list" },
      { text: "What is late, and what is blocked", tag: "Risk list" },
      { text: "Ask the Division Lead, or the named project lead on a project they lead. Never the executive", tag: "Right person" },
      { text: "Blocked by the client for more than 3 working days: go to card O4", tag: "Handled" },
      { text: "Blocked inside the agency: take it to the Division Leads involved", tag: "Raised" },
      { text: "Update the dashboard before you leave", tag: "Current" },
      { text: "Every Friday: look at next week. Is anything already at risk?", tag: "Next week checked" },
    ],
    finishedWhen: "The dashboard is current, and nothing late has been found by the client before us.",
  },
  O4: {
    title: "Chase a delay or a missing input",
    before: [
      "Something is late or waiting",
    ],
    steps: [
      { text: "Work out whether it is waiting on the client, or on us", tag: "Known" },
      { text: "CLIENT: message them in the agreed channel with exactly what is needed and by when", tag: "Asked" },
      { text: "Past 3 working days, the delivery date moves by the same number of days. Tell the client the same day, plainly, not as a complaint", tag: "Date moved" },
      { text: "Past 10 working days, take it out of this month and raise it at the monthly review", tag: "Removed" },
      { text: "Log every delay: what, how long, why. It goes in the monthly report", tag: "Logged" },
      { text: "US: ask the Division Lead what is stuck and what they need", tag: "Asked" },
      { text: "If two divisions are waiting on each other, get both Division Leads together. Not their executives", tag: "Unblocked" },
      { text: "If it will miss a client date, tell the founders before the client notices", tag: "Escalated" },
    ],
    finishedWhen: "The delay is logged, the right person knows, and the client heard any date change from us first.",
  },
  O5: {
    title: "Weekly client update",
    before: [
      "Each Division Lead's weekly update, by Friday 2 PM",
    ],
    steps: [
      { text: "Collect each Division Lead's weekly update by Friday 2 PM. On a project with a named project lead, collect it from the project lead", tag: "Updates in" },
      { text: "Each update uses the same categories as the divisional scope", tag: "Consistent" },
      { text: "Chase anyone missing at 2 PM. Do not wait", tag: "Chased" },
      { text: "Put it together: what got done, what is next week, what is waiting on the client", tag: "Draft" },
      { text: "Use the report format agreed with this client at onboarding (card O1, step 9), if one was agreed", tag: "Right format" },
      { text: "Send it before the weekly client call", tag: "Sent" },
      { text: "Anything the client says on the call that changes the plan, tell the Division Leads and project leads the same day", tag: "Passed on" },
    ],
    finishedWhen: "The client has the update before the call, and any change from the call has reached the Division Leads the same day.",
  },
  O6: {
    title: "Monthly client report",
    before: [
      "Each Division Lead's monthly report, between the 25th and 28th",
    ],
    steps: [
      { text: "Collect each Division Lead's monthly report between the 25th and 28th. On a project with a named project lead, collect it from the project lead", tag: "Reports in" },
      { text: "Check each one covers what was planned, what was delivered, the results, and what is next", tag: "Checked" },
      { text: "Build one master report for the client", tag: "Master report" },
      { text: "Include the delay log: what we waited on and for how long. Factual, not a complaint", tag: "Delays shown" },
      { stop: "Founders review before it goes to the client" },
      { text: "Send to the client", tag: "Sent" },
      { text: "Use it to prepare next month's scope with the founders", tag: "Feeds next month" },
    ],
    finishedWhen: "One master report sent, including the delay log, and it has fed next month's scope.",
  },
  O7: {
    title: "Monthly workflow check",
    before: [
      "A month of finished work",
      "The gaps log",
    ],
    steps: [
      { text: "Pick five finished jobs from each division", tag: "Sample" },
      { text: "For each one: were the STOPs respected, was the right card followed, is it in the right folder", tag: "Checked" },
      { text: "Write down what was skipped and why. Usually the reason is a bad card, not a bad person", tag: "Findings" },
      { text: "Go through the gaps log: every time someone found the workflows did not cover something", tag: "Gaps reviewed" },
      { text: "Take the findings to Sureet", tag: "Reviewed" },
      { text: "Update the cards that need it, and tell everyone which cards changed", tag: "Cards updated" },
    ],
    finishedWhen: "Findings reviewed with Sureet, affected cards updated, and everyone told what changed.",
  },
  O8: {
    title: "Before new work is agreed with a client",
    before: [
      "A founder is about to agree new work",
    ],
    steps: [
      { text: "Find out which divisions the new work touches", tag: "Divisions known" },
      { text: "Ask each Division Lead: can you do this, and by when?", tag: "Asked" },
      { text: "Check the dashboard for what they already have", tag: "Load checked" },
      { text: "Tell the founders plainly: yes, yes with a later date, or not without help", tag: "Answer given" },
      { stop: "Founders agree the new work only after hearing this answer" },
    ],
    finishedWhen: "The founders know, before agreeing, whether the team can actually deliver it and by when.",
  },
  O9: {
    title: "A client is leaving",
    before: [
      "Confirmation that the engagement is ending",
    ],
    steps: [
      { text: "Confirm the last working day and what we still owe", tag: "End date" },
      { text: "List everything we set up or hold access to, division by division", tag: "Inventory" },
      { text: "Each division hands over its part using its own handover job", tag: "Handed over" },
      { text: "Give the client their files, their data, and a short note on how things are set up", tag: "Delivered" },
      { text: "Remove our access only AFTER the client confirms theirs works", tag: "Access removed" },
      { stop: "Founders approve the handover" },
      { text: "Send the final report", tag: "Closed" },
      { text: "Write down what we learned from the account", tag: "Lessons" },
    ],
    finishedWhen: "The client has everything, our access is gone, and the lessons are written down.",
  },
};
